'use client';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import type {HeaderPreset, NavContent} from '@olympus-frontend/tenant-config';
import {useSiteConfig} from '@olympus-frontend/tenant-config';
import {usePathname} from 'next/navigation';
import {useEffect, useRef, useState, type ElementType} from 'react';
import type {Locale} from '../../shared/constants/locales';
import {DEVELOPMENTS_NAV_KEY, type DestinationsMenuData} from './DestinationsMenu';
import {HeaderDesktop} from './HeaderDesktop';
import {HeaderMobile} from './HeaderMobile';
import {HEADER_OVERLAY_ATTR} from './headerOverlay';

interface HeaderProps {
  content: NavContent;
  locale: Locale;
  // Resolved by a server component so the runtime DISABLED_LOCALES value reaches
  // the client bundle, which cannot read it itself.
  locales: readonly Locale[];
  developments?: DestinationsMenuData;
  // Endpoint serving the same menu on a shorter cache window. The page HTML itself is cached for
  // much longer, so without this the menu would only change when the whole page is re-rendered.
  developmentsEndpoint?: string;
  preset?: HeaderPreset;
}

const SCROLLED_ATTR = 'data-scrolled';
const SCROLLED_THRESHOLD = 80;
const LAYER = {
  content: '""',
  position: 'absolute',
  inset: 0,
  zIndex: -1,
  pointerEvents: 'none',
  '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
} as const;

const HeaderRoot = styled(Box)<{component?: ElementType}>(({theme}) => ({
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
  background: theme.palette.colors.surface.header.default,
  color: theme.palette.colors.text.neutral.inverse,
  // A pinned hero renders the overlay attribute, and the header then floats over it:
  // transparent at the top, a blurred bar once the page has scrolled under it.
  [`html:has([${HEADER_OVERLAY_ATTR}]) &`]: {
    position: 'fixed',
    insetInline: 0,
    background: 'transparent',
    // The scrolled bar is the header's own colour — the overlay only buys the hero a
    // transparent header at the top of the page, not a different header afterwards.
    '&::before': {
      ...LAYER,
      background: theme.palette.colors.surface.header.default,
      transform: 'translateY(-102%)',
      opacity: 0,
      transition: [
        theme.transitions.create('transform', {duration: 800, easing: 'cubic-bezier(.25,.74,.22,.99)'}),
        theme.transitions.create('opacity', {duration: 600, easing: 'ease'}),
      ].join(','),
    },
    // Keeps the nav legible over arbitrary hero media while the header is transparent;
    // once the solid bar is in it has nothing left to do, and painting over the bar
    // would tint it away from the header colour every other page renders.
    '&::after': {
      ...LAYER,
      zIndex: -2,
      backgroundImage: `linear-gradient(${theme.palette.colors.surface.darkOverlayStrong}, transparent)`,
      transition: theme.transitions.create('opacity'),
    },
    [`&[${SCROLLED_ATTR}]::before`]: {transform: 'none', opacity: 1},
    [`&[${SCROLLED_ATTR}]::after`]: {opacity: 0},
  },
}));

function useScrolledAttribute() {
  const ref = useRef<HTMLElement>(null);
  // Re-read per route: only a page with a pinned hero renders the overlay attribute,
  // and every other page would otherwise run this listener for a style nothing reads.
  const pathname = usePathname();
  useEffect(() => {
    if (!document.querySelector(`[${HEADER_OVERLAY_ATTR}]`)) {
      ref.current?.removeAttribute(SCROLLED_ATTR);
      return;
    }
    const sync = () => {
      ref.current?.toggleAttribute(SCROLLED_ATTR, window.scrollY > SCROLLED_THRESHOLD);
    };
    sync();
    window.addEventListener('scroll', sync, {passive: true});
    return () => {
      window.removeEventListener('scroll', sync);
    };
  }, [pathname]);
  return ref;
}

export function Header({content, locale, locales, developments, developmentsEndpoint, preset}: HeaderProps) {
  const {branding, contact, presets} = useSiteConfig();
  const rootRef = useScrolledAttribute();
  const resolvedPreset = preset ?? presets.header;
  const refreshed = useRefreshedDevelopments(developmentsEndpoint, locale);
  const menu = refreshed ?? developments;
  // 'developments' preset: the Developments entry opens the mega-menu/accordion
  // when data exists and falls back to a plain link otherwise. 'basic' preset:
  // the Developments entry is dropped and the menu never mounts.
  const showDevelopments = resolvedPreset === 'developments' && Boolean(menu && menu.countries.length > 0);
  const links = resolvedPreset === 'basic' ? content.links.filter(link => link.key !== DEVELOPMENTS_NAV_KEY) : content.links;

  const contentData = {
    branding,
    content: {...content, links},
    locale,
    locales,
    hotline: contact?.hotline,
    whatsappHref: contact?.whatsappHref,
    developments: menu,
    showDevelopments,
  };

  return (
    <HeaderRoot component="header" ref={rootRef}>
      <Container>
        <HeaderDesktop {...contentData} />
        <HeaderMobile {...contentData} />
      </Container>
    </HeaderRoot>
  );
}

Header.displayName = 'Header';

function useRefreshedDevelopments(endpoint: string | undefined, locale: Locale): DestinationsMenuData | undefined {
  const [menu, setMenu] = useState<DestinationsMenuData>();

  useEffect(() => {
    if (!endpoint) {
      return;
    }
    const controller = new AbortController();
    fetch(`${endpoint}?locale=${locale}`, {signal: controller.signal})
      .then(response => (response.ok ? (response.json() as Promise<DestinationsMenuData>) : undefined))
      .then(data => {
        // A non-OK response or a menu with no countries/projects (a backend blip the endpoint
        // could not distinguish from "nothing configured") must not overwrite the valid
        // server-rendered menu — only replace it once the refresh actually has data.
        if (data && (data.countries.length > 0 || data.projects.length > 0)) {
          setMenu(data);
        }
      })
      .catch(() => undefined);
    return () => {
      controller.abort();
    };
  }, [endpoint, locale]);

  return menu;
}
